/**
 * Auth labels: backend-driven page content for Sign In, Register, Lockout, Validation.
 * CHP2-10244: One request per brand session; fallbacks to constants when API content is missing.
 */
import { serverUrl } from '@utils/MkEnv'
import { API } from '@constants'
import { fetchApi } from '@api/MkApi'
import { createLogger } from '@utils/Logger'

export { themeBrandToPageContentBrand } from '@utils/pageContentBrand'

const logger = createLogger('AuthLabels')

export const AUTH_LABELS_KIND = 'authLabels'

/**
 * Backend GET /page-content/{kind} response shape.
 * flows[flow][step] = parsed content object (e.g. { title, subtitle, submitButton, ... }).
 */
export type PageContentResponse = {
  flows: {
    [flow: string]: {
      [step: string]: Record<string, unknown>
    }
  }
}

const inMemoryCache = new Map<string, PageContentResponse>()

const SESSION_STORAGE_KEY_PREFIX = 'authLabels:'

/**
 * Build URL for GET page-content/authLabels?brand=<BRAND_IDENTIFIER>.
 */
export function getAuthLabelsPageContentUrl(brand: string): string {
  return `${serverUrl}${API}/page-content/${AUTH_LABELS_KIND}?brand=${encodeURIComponent(brand)}`
}

/**
 * Get a single label from flows with fallback. Normalizes doubled apostrophes ('' → ').
 * path can be dot-separated for nested keys (e.g. 'actions.callUs').
 */
export function getAuthLabel(flows: PageContentResponse['flows'] | null | undefined, flow: string, step: string, path: string, fallback: string): string {
  if (!flows?.[flow]?.[step]) return fallback
  const stepContent = flows[flow][step] as Record<string, unknown>
  const value = path.split('.').reduce<unknown>((acc, part) => {
    if (acc === null || acc === undefined || typeof acc !== 'object') return null
    return (acc as Record<string, unknown>)[part]
  }, stepContent)
  if (typeof value !== 'string') return fallback
  return value.replace(/''/g, "'")
}

export type GetAuthLabelsPageContentOptions = {
  /** When true, skip in-memory hit and fetch from the network (updates caches on success). */
  revalidate?: boolean
}

/**
 * Fetch auth labels from backend. Caches in memory and optionally in sessionStorage.
 * On failure returns null and logs non-fatal warning.
 */
export async function getAuthLabelsPageContent(brandEnumName: string, options?: GetAuthLabelsPageContentOptions): Promise<PageContentResponse | null> {
  if (!options?.revalidate && inMemoryCache.has(brandEnumName)) {
    return inMemoryCache.get(brandEnumName) ?? null
  }

  const url = getAuthLabelsPageContentUrl(brandEnumName)
  try {
    const data = await fetchApi<PageContentResponse>(url)
    if (data?.flows) {
      inMemoryCache.set(brandEnumName, data)
      try {
        sessionStorage.setItem(`${SESSION_STORAGE_KEY_PREFIX}${brandEnumName}`, JSON.stringify(data))
      } catch {
        // sessionStorage full or unavailable
      }
      return data
    }
  } catch (e) {
    logger.warn('Auth labels API request failed', { url, error: e })
  }
  return null
}

/**
 * Return cached auth labels from sessionStorage if present (e.g. after reload).
 * Does not trigger a network request.
 */
export function getCachedAuthLabelsFromSession(brandEnumName: string): PageContentResponse | null {
  if (inMemoryCache.has(brandEnumName)) {
    return inMemoryCache.get(brandEnumName) ?? null
  }
  try {
    const raw = sessionStorage.getItem(`${SESSION_STORAGE_KEY_PREFIX}${brandEnumName}`)
    if (raw) {
      const data = JSON.parse(raw) as PageContentResponse
      if (data?.flows) {
        inMemoryCache.set(brandEnumName, data)
        return data
      }
    }
  } catch {
    // ignore
  }
  return null
}

export { inMemoryCache as authLabelsCache }

/** Map frontend Sign In step keys to backend signIn flow step names */
export const SIGNIN_STEP_TO_BACKEND: Record<string, string> = {
  signin: 'loginForm',
  'help-panel': 'helpPanel',
  'forgot-username-confirmation': 'usernameRecovered',
  'forgot-password-reset-link': 'passwordRecoveryEmailSent',
  'forgot-password-reset-form': 'resetPassword',
  'account-locked': 'accountLocked',
}

/** Map frontend Register step names to backend register flow step names */
export const REGISTER_STEP_TO_BACKEND: Record<string, string> = {
  accountLookup: 'accountLookup',
  userDetails: 'userDetails',
  checkEmail: 'completion',
  setPassword: 'setPassword',
  mfaSetupSelector: 'mfaSetupSelector',
  mfaSetupDetails: 'mfaSetupDetails',
  mfaVerifyCode: 'mfaVerifyCode',
  accountLockScreen: 'accountLock',
  mfaCriticalLockScreen: 'accountLock',
  activationError: 'activationError',
}
